import { useState, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DataState, CardListSkeleton } from '@/shared/ui';
import { RocketIcon } from '@/shared/ui/radix-icons';
import { PageHeader } from '@features/property-management/components/PageHeader';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAuth } from '@context/authentication';
import { getCurrentUserRole, isOperationalOrHigher } from '@/shared/utils/permissions';
import { useLaunchList } from '../hooks/useLaunchList';
import { StickyHeaderBar } from '@/features/property-management/components/StickyHeaderBar';
import { useIsMobile } from '@/shared/hooks';
import { getListingPageLayout } from '@/shared/ui/layout/listing-page-layout';
import { LaunchesListView } from '../components/LaunchesListView';
import { LaunchesService } from '../services/launchesApi';
import '../styles/launch-tokens.css';
import { navIconFor } from '@layout/agency/agency-nav';

const CreateLaunchDrawer = lazy(() =>
  import('../components/CreateLaunchDrawer').then((m) => ({ default: m.CreateLaunchDrawer })),
);

const SK = 'animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]';

/**
 * Loading placeholder for the launch detail header.
 */
function LaunchHeaderSkeleton() {
  return (
    <div
      style={{
        padding: 'var(--space-4) var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className={`${SK} h-7 w-40`} />
        <div className={`${SK} h-9 w-36`} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <div className={`${SK} h-10 flex-1`} />
      </div>
    </div>
  );
}

/**
 * Main launches page with PageHeader and list view.
 */
export function LaunchesPage() {
  const { t } = useTranslation('features/launches');
  const { agency_id: agencyId } = useParams<{ agency_id: string }>();
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { agency } = useAgency((s) => s);
  const { user } = useAuth();
  const canManage = isOperationalOrHigher(
    getCurrentUserRole(agency, user?.profile?.email),
  );
  const { launches, filtered, filter, setFilter, isLoading, isError, retry, kpis } = useLaunchList(agencyId ?? '');
  const isMobile = useIsMobile();
  const layout = getListingPageLayout(isMobile);

  const searched = search
    ? filtered.filter((l) => l.name.toLowerCase().includes(search.toLowerCase()))
    : filtered;

  const openBoard = (launchId: string) => {
    navigate(launchId);
  };

  const publishLaunch = async (launchId: string) => {
    if (!agencyId) return;
    try {
      await LaunchesService.open(agencyId, launchId);
      toast.success(t('list.publishSuccess'));
      retry();
    } catch {
      toast.error(t('errors.generic'));
    }
  };

  const closeLaunch = async (launchId: string) => {
    if (!agencyId) return;
    try {
      await LaunchesService.close(agencyId, launchId);
      toast.success(t('board.closeSuccess'));
      retry();
    } catch {
      toast.error(t('errors.generic'));
    }
  };

  return (
    <div className="launch-scope" style={layout.rootStyle}>
      <div style={layout.paddingStyle}>
        <div style={layout.stackStyle}>
      {!isLoading && (
        <StickyHeaderBar>
        <PageHeader
          title={t('list.title')}
          icon={navIconFor('launches', 20)}
          ctaLabel={canManage ? t('list.create') : undefined}
          searchPlaceholder={t('list.searchPlaceholder')}
          onCtaClick={canManage ? () => setShowCreate(true) : undefined}
          onSearchChange={setSearch}
          introLabel={t('intro.label')}
          introBody={t('list.introBody')}
          introTestIdPrefix="launches"
          hideFilters
        />
        </StickyHeaderBar>
      )}

      {/* getListingPageLayout's desktop rootStyle is overflow:hidden by
          design — it expects the inner list to be the scroller. Without
          this wrapper nothing scrolled and everything past the fold was
          simply clipped. */}
      <div className={layout.scrollContainerClassName}>
      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={searched.length === 0 && !isLoading}
        onRetry={retry}
        loadingFallback={<><LaunchHeaderSkeleton /><div style={{ marginTop: 'var(--space-4)' }}><CardListSkeleton count={4} /></div></>}
      >
        <LaunchesListView
          launches={searched}
          filter={filter}
          onFilterChange={setFilter}
          onOpenLaunch={openBoard}
          onPublishLaunch={(id) => void publishLaunch(id)}
          onCloseLaunch={(id) => void closeLaunch(id)}
          onCreateLaunch={() => setShowCreate(true)}
          hasActiveQuery={search.trim().length > 0}
          hasAnyLaunches={launches.length > 0}
          canManage={canManage}
          kpis={kpis}
        />
      </DataState>
      </div>

      <Suspense fallback={null}>
        <CreateLaunchDrawer
          agencyId={agencyId ?? ''}
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); retry(); }}
        />
      </Suspense>
        </div>
      </div>
    </div>
  );
}
